import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router";
import { Button, ThemeToggle } from "@mieweb/ui";
import { BrandLockup } from "../components/BrandMark";
import { CodeBlock } from "../components/CodeBlock";
import { isDesktop } from "../lib/desktop";
import { FAQSection } from "../components/FAQSection";
import type { FAQItem } from "../components/FAQSection";

const docsFAQs: FAQItem[] = [
  {
    question: "Do I need a build step to use the widget?",
    answer:
      "No. The Pollenate widget is a standalone script you add via a <script> tag. It works on any website — static HTML, WordPress, React, Next.js, or any other framework — with zero build configuration.",
  },
  {
    question: "What is an inbox key vs. an API key?",
    answer:
      'An inbox key identifies which inbox receives the feedback. An API key authenticates the request. You need both: the inbox key (from the Inboxes page) tells Pollenate where to route the feedback, and the API key (from Settings → API Keys with the "collect" scope) authorizes the submission.',
  },
  {
    question: "How do I authenticate API requests?",
    answer:
      "For widget and server-to-server integrations, pass your API key in the X-Pollenate-Key header. For dashboard API access, use a JWT Bearer token obtained via OTP email verification in the Authorization header.",
  },
  {
    question: "Can I use Pollenate with React?",
    answer:
      "Yes! Install @pollenate/react (npm install @pollenate/react) for a fully typed, drop-in React component. See the React Package section in the docs above. You can also use the vanilla script tag widget directly in any React app.",
  },
  {
    question: "What widget types are available?",
    answer:
      "Pollenate supports six widget types: thumbs (up/down), stars (1–5 rating), NPS (0–10 scale), CSAT (1–5 satisfaction), emoji (sentiment reactions), and text (open-ended comments). Set the type via the data-type attribute or the type configuration option.",
  },
  {
    question: "How does the semantic search API work?",
    answer:
      "POST a natural language query to /organizations/:orgId/search. Pollenate generates an AI embedding of your query and performs vector similarity search against all stored feedback, returning the most semantically relevant results. You can scope the search to a specific brand or inbox, and results fall back to keyword matching when no semantic matches are found.",
  },
  {
    question: "Is there a rate limit on the collect endpoint?",
    answer:
      "The Starter plan allows up to 5,000 events per month. Team plans support 100,000 events per month. Enterprise plans have no hard limits. All plans include burst protection to prevent abuse.",
  },
  {
    question: "Can I listen for widget events in my app?",
    answer:
      'Yes. The widget emits a "pollenate:submit" CustomEvent on the window object whenever feedback is submitted. Listen for it with window.addEventListener("pollenate:submit", callback) to integrate with your analytics or trigger custom behavior.',
  },
  {
    question: "How are Feedback Pages different from widgets?",
    answer:
      "Widgets are lightweight embeds you drop into existing pages for quick, single-question feedback. Feedback Pages are standalone, multi-question forms hosted at their own URL. Use Feedback Pages when you need multi-question surveys, want to share via QR code or print, or need a branded form that stands on its own without embedding into an existing site.",
  },
  {
    question: "Can I embed a Feedback Page on my website?",
    answer:
      "Yes! Each published Feedback Page can be embedded using an iframe. Append ?embed=true to the URL to remove headers and footers for a clean embed. You can also share Feedback Pages via direct link, QR code, email signature button, or a print-ready flyer.",
  },
  {
    question: 'Why is my widget showing "Failed to fetch" errors?',
    answer:
      "This usually means your site's Content Security Policy (CSP) is blocking the widget from connecting to the Pollenate API. You need to add pollenate.dev and api.pollenate.dev to your CSP connect-src and script-src directives. See the Content Security Policy section in the docs for full details.",
  },
  {
    question: "How do I get notified when new feedback comes in?",
    answer:
      "Create an automation rule under Dashboard → Automations. Choose the feedback.created event, optionally add filters (such as low score or a written comment), and pick an action: email, webhook, or Zapier. Webhook and Zapier rules deliver in real time.",
  },
  {
    question: "How do I verify that a webhook really came from Pollenate?",
    answer:
      "Set a signing secret on the webhook rule. Pollenate signs the raw request body with HMAC-SHA256 and sends the hex digest in the X-Pollenate-Signature header as sha256=<digest>. Recompute the HMAC on your side using the same secret and compare with a constant-time check before trusting the payload.",
  },
  {
    question: "Can a webhook create a GitHub issue (or Jira/Linear ticket)?",
    answer:
      "Yes — directly. Store your API token in the secrets store, then add an Authorization header (Bearer {{secrets.GITHUB_TOKEN}}) and a body template to a webhook automation pointed at GitHub's API. No relay or extra service is needed. You can also use the Zapier app's Create Issue action, or send to your own relay. See the Recipe: Create a GitHub Issue section.",
  },
  {
    question: "Why won't Pollenate deliver to my localhost webhook?",
    answer:
      "To prevent SSRF, webhooks only deliver to publicly reachable hosts. Requests to localhost, loopback, link-local, or private IP ranges (10.x, 192.168.x, 172.16–31.x) are rejected. For local development, expose your endpoint with a tunnel such as ngrok or cloudflared.",
  },
];

const SECTION_IDS = [
  "how-it-works",
  "quick-start",
  "authentication",
  "widget-embed",
  "widget-types",
  "widget-customization",
  "react-package",
  "custom-widgets",
  "feedback-pages",
  "feedback-pages-builder",
  "feedback-pages-google-forms",
  "feedback-pages-sharing",
  "feedback-pages-api",
  "api-overview",
  "api-feedback",
  "api-inboxes",
  "api-search",
  "ai-agents",
  "ai-skill-file",
  "ai-mcp-server",
  "ai-examples",
  "automations",
  "automation-events",
  "webhooks",
  "webhook-secrets",
  "webhook-github-issue",
  "zapier",
  "integrations",
  "content-security-policy",
  "docs-faq",
];

export function DocsPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("");
  const location = useLocation();

  // Scroll to anchor on mount or when hash changes
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      // Small delay to ensure DOM is rendered
      const timer = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  // Scrollspy: observe sections and highlight current one in sidebar
  useEffect(() => {
    const elements = SECTION_IDS.map((id) =>
      document.getElementById(id),
    ).filter(Boolean) as HTMLElement[];
    if (elements.length === 0) return;

    // Use a scroll listener approach for reliability across all viewport sizes
    const handleScroll = () => {
      let current = "";
      for (const el of elements) {
        const rect = el.getBoundingClientRect();
        // Section is "active" when its top is within 120px of the viewport top
        if (rect.top <= 120) {
          current = el.id;
        }
      }
      setActiveSection(current || elements[0]?.id || "");
    };

    // Run once immediately
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-neutral-950">
      {/* Navigation */}
      <nav className="bg-white dark:bg-neutral-950 border-b border-slate-200 dark:border-neutral-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <BrandLockup
                iconClassName="h-7 w-7"
                textClassName="text-xl font-bold text-slate-900 dark:text-white"
              />
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              {!isDesktop && (
                <Link
                  to="/download"
                  className="hidden sm:inline text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors text-sm"
                >
                  Download
                </Link>
              )}
              <a
                href="/openapi.yaml"
                target="_blank"
                className="hidden sm:inline text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors text-sm"
              >
                OpenAPI Spec
              </a>
              <ThemeToggle mode="two-way" size="sm" variant="ghost" />
              <Link to="/login?mode=signup">
                <Button className="text-xs sm:text-sm whitespace-nowrap">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Documentation</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Learn how Pollenate works — whether you're setting up your first feedback widget, building a survey,
            or connecting it to your app. No coding experience required to get started.
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Mobile sidebar toggle */}
          <div className="md:hidden">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="flex items-center gap-2 w-full px-4 py-3 bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl text-slate-700 dark:text-neutral-200 font-medium text-sm"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              Navigation
              <svg
                className={`w-4 h-4 ml-auto transition-transform ${sidebarOpen ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </div>

          {/* Sidebar */}
          <nav
            className={`md:col-span-1 ${sidebarOpen ? "block" : "hidden"} md:block`}
          >
            <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto space-y-6 pr-2">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  Getting Started
                </h3>
                <ul className="space-y-1">
                  <li>
                    <a
                      href="#how-it-works"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "how-it-works" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      How It Works
                    </a>
                  </li>
                  <li>
                    <a
                      href="#quick-start"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "quick-start" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Quick Start
                    </a>
                  </li>
                  <li>
                    <a
                      href="#authentication"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "authentication" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Authentication
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  Widget
                </h3>
                <ul className="space-y-1">
                  <li>
                    <a
                      href="#widget-embed"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "widget-embed" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Embed Widget
                    </a>
                  </li>
                  <li>
                    <a
                      href="#widget-types"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "widget-types" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Widget Types
                    </a>
                  </li>
                  <li>
                    <a
                      href="#widget-customization"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "widget-customization" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Customization
                    </a>
                  </li>
                  <li>
                    <a
                      href="#react-package"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "react-package" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      React Package
                    </a>
                  </li>
                  <li>
                    <a
                      href="#custom-widgets"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "custom-widgets" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Custom Widgets
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  Feedback Pages
                </h3>
                <ul className="space-y-1">
                  <li>
                    <a
                      href="#feedback-pages"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "feedback-pages" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Overview
                    </a>
                  </li>
                  <li>
                    <a
                      href="#feedback-pages-builder"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "feedback-pages-builder" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Form Builder
                    </a>
                  </li>
                  <li>
                    <a
                      href="#feedback-pages-google-forms"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "feedback-pages-google-forms" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Import from Google Forms
                    </a>
                  </li>
                  <li>
                    <a
                      href="#feedback-pages-sharing"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "feedback-pages-sharing" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Sharing & Embedding
                    </a>
                  </li>
                  <li>
                    <a
                      href="#feedback-pages-api"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "feedback-pages-api" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Pages API
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  API Reference
                </h3>
                <ul className="space-y-1">
                  <li>
                    <a
                      href="#api-overview"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "api-overview" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Overview
                    </a>
                  </li>
                  <li>
                    <a
                      href="#api-feedback"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "api-feedback" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Feedback
                    </a>
                  </li>
                  <li>
                    <a
                      href="#api-inboxes"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "api-inboxes" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Inboxes
                    </a>
                  </li>
                  <li>
                    <a
                      href="#api-search"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "api-search" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Semantic Search
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  AI Agents
                </h3>
                <ul className="space-y-1">
                  <li>
                    <a
                      href="#ai-agents"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "ai-agents" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Overview
                    </a>
                  </li>
                  <li>
                    <a
                      href="#ai-skill-file"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "ai-skill-file" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Skill File
                    </a>
                  </li>
                  <li>
                    <a
                      href="#ai-mcp-server"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "ai-mcp-server" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      MCP Server
                    </a>
                  </li>
                  <li>
                    <a
                      href="#ai-examples"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "ai-examples" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Agent Examples
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  Automations &amp; Webhooks
                </h3>
                <ul className="space-y-1">
                  <li>
                    <a
                      href="#automations"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "automations" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Overview
                    </a>
                  </li>
                  <li>
                    <a
                      href="#automation-events"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "automation-events" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Events &amp; Filters
                    </a>
                  </li>
                  <li>
                    <a
                      href="#webhooks"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "webhooks" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Webhooks
                    </a>
                  </li>
                  <li>
                    <a
                      href="#webhook-secrets"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "webhook-secrets" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Secrets &amp; Custom Headers
                    </a>
                  </li>
                  <li>
                    <a
                      href="#webhook-github-issue"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "webhook-github-issue" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Recipe: Create a GitHub Issue
                    </a>
                  </li>
                  <li>
                    <a
                      href="#zapier"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "zapier" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Zapier
                    </a>
                  </li>
                  <li>
                    <a
                      href="#integrations"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "integrations" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      More Integrations
                    </a>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  Troubleshooting
                </h3>
                <ul className="space-y-1">
                  <li>
                    <a
                      href="#content-security-policy"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "content-security-policy" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      Content Security Policy
                    </a>
                  </li>
                  <li>
                    <a
                      href="#docs-faq"
                      onClick={() => setSidebarOpen(false)}
                      className={`block py-2 transition-colors border-l-2 pl-3 ${activeSection === "docs-faq" ? "text-amber-600 dark:text-amber-400 border-amber-500 font-medium" : "text-slate-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 border-transparent"}`}
                    >
                      FAQ
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </nav>

          {/* Main content */}
          <div className="md:col-span-3 prose prose-slate dark:prose-invert max-w-none min-w-0">
            {/* How It Works */}
            <section id="how-it-works" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  💡
                </span>
                How Pollenate Works
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Pollenate helps you hear from the people who use your product, visit your website, or walk through your door.
                It collects their feedback — ratings, comments, survey responses — and organizes it all in one dashboard so you can spot patterns and take action.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                  <div className="text-2xl mb-2">🏢</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">Organizations &amp; Brands</h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Your <strong>organization</strong> is your team's account — it holds everything. Inside it, you can create <strong>brands</strong> to separate feedback
                    by product, department, or location. For example, a hospital group might have one brand per clinic.
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                  <div className="text-2xl mb-2">📬</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">Inboxes</h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Think of an <strong>inbox</strong> like a mailbox for feedback. You might have one inbox for your website, another for your mobile app,
                    and a third for post-visit surveys. Each inbox has its own unique key so feedback gets routed to the right place.
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                  <div className="text-2xl mb-2">🔌</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">Widgets</h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    A <strong>widget</strong> is a small feedback prompt that appears on your website — like a "How did we do?" button
                    or a row of star ratings. You add it by pasting a short snippet of code. No app installation or software download needed.
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                  <div className="text-2xl mb-2">📋</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">Feedback Pages</h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    <strong>Feedback Pages</strong> are standalone survey forms with their own web address. Great when you want to ask
                    multiple questions, share a link via email or text message, or print a QR code on a flyer for in-person events.
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                  <div className="text-2xl mb-2">🔑</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">API Keys</h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    An <strong>API key</strong> is like a password that lets your website talk to Pollenate securely.
                    You create one in <em>Settings → API Keys</em> and give it to your widget so it has permission to send feedback to your account.
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                  <div className="text-2xl mb-2">📊</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">Dashboard &amp; Reports</h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Everything your users submit shows up in your <strong>dashboard</strong> in real time. You can filter by date,
                    score, or inbox, search by keyword, and watch trends over time — no spreadsheets required.
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/40 rounded-xl p-6">
                <h3 className="font-semibold text-amber-900 dark:text-amber-300 mb-2">The big picture</h3>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm text-amber-800 dark:text-amber-300">
                  <span className="bg-amber-200 dark:bg-amber-800/50 rounded-full px-3 py-1">Create an inbox</span>
                  <span>→</span>
                  <span className="bg-amber-200 dark:bg-amber-800/50 rounded-full px-3 py-1">Add a widget or survey</span>
                  <span>→</span>
                  <span className="bg-amber-200 dark:bg-amber-800/50 rounded-full px-3 py-1">People give feedback</span>
                  <span>→</span>
                  <span className="bg-amber-200 dark:bg-amber-800/50 rounded-full px-3 py-1">Review in your dashboard</span>
                  <span>→</span>
                  <span className="bg-amber-200 dark:bg-amber-800/50 rounded-full px-3 py-1">Take action</span>
                </div>
              </div>
            </section>

            {/* Quick Start */}
            <section id="quick-start" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🚀
                </span>
                Quick Start
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                Get up and running with Pollenate in under 5 minutes. Collect
                your first piece of feedback today.
              </p>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> You paste a small snippet of code into your website. It adds a feedback button
                  that visitors can click to rate their experience or leave a comment. Their responses appear in your Pollenate dashboard instantly.
                  If you're not comfortable editing code, ask your web developer to add this — it only takes a minute.
                </p>
              </div>

              <CodeBlock
                language="html"
                label="1. Add the widget script to your HTML"
                code={`<script 
  src="https://pollenate.dev/widget.js"
  data-inbox-key="YOUR_INBOX_KEY"
  data-api-key="YOUR_API_KEY"
  data-type="emoji"
  async
></script>`}
              />

              <p className="text-slate-600 dark:text-neutral-400 mt-6">
                That's it! The widget will automatically appear on your page.
                You'll need both an inbox key (from the Inboxes page) and an API
                key with the "collect" scope (from Settings → API Keys).
              </p>
            </section>

            {/* Authentication */}
            <section id="authentication" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🔐
                </span>
                Authentication
              </h2>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> Authentication is how Pollenate knows the feedback is coming from <em>your</em> website
                  and not someone else's. It uses special keys — kind of like a password — that you copy from your Pollenate settings
                  and paste into your widget code. There are two kinds: one for your website's feedback widget, and one for managing your account through code.
                </p>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Pollenate uses two authentication methods depending on your use
                case:
              </p>

              <div className="grid gap-4 mb-6">
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6 overflow-hidden">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                    API Keys (for widgets & integrations)
                  </h3>
                  <p className="text-slate-600 dark:text-neutral-400 text-sm mb-3">
                    Use API keys for server-to-server integrations and widget
                    authentication. Pass your key in the{" "}
                    <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                      X-Pollenate-Key
                    </code>{" "}
                    header.
                  </p>
                  <CodeBlock
                    language="bash"
                    theme="auto"
                    code={`curl -X POST https://api.pollenate.dev/collect \\
  -H "X-Pollenate-Key: pk_live_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{"type": "emoji", "score": 5, "comment": "Great!"}'`}
                  />
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6 overflow-hidden">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                    JWT Bearer Token (for dashboard API)
                  </h3>
                  <p className="text-slate-600 dark:text-neutral-400 text-sm mb-3">
                    Use JWT tokens for authenticated user sessions. Tokens are
                    obtained via OTP email verification.
                  </p>
                  <CodeBlock
                    language="bash"
                    theme="auto"
                    code={`curl https://api.pollenate.dev/organizations \\
  -H "Authorization: Bearer eyJhbGc..."`}
                  />
                </div>
              </div>
            </section>

            {/* Widget Embed */}
            <section id="widget-embed" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  📦
                </span>
                Embed Widget
              </h2>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> A widget is a small interactive element that appears on your website — like a row of smiley faces
                  or a star rating — asking visitors for their opinion. You add it by pasting one line of code into your website.
                  It works on any website: WordPress, Squarespace, Wix, custom-built sites, and more. Your web developer can set it up in under a minute.
                </p>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                The Pollenate widget can be embedded in any website with a
                single script tag.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Script Attributes
              </h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">Attribute</th>
                      <th className="text-left p-3 font-semibold">Required</th>
                      <th className="text-left p-3 font-semibold">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          data-inbox-key
                        </code>
                      </td>
                      <td className="p-3">Yes</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Your inbox's public key (from the Inboxes page)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          data-api-key
                        </code>
                      </td>
                      <td className="p-3">Yes</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Your API key with "collect" scope (from Settings → API
                        Keys)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          data-type
                        </code>
                      </td>
                      <td className="p-3">No</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Widget type: emoji, stars, thumbs, nps, csat, text
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          data-position
                        </code>
                      </td>
                      <td className="p-3">No</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Position: bottom-right, bottom-left, top-right, top-left
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          data-theme
                        </code>
                      </td>
                      <td className="p-3">No</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Theme: light, dark, or custom theme ID
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          data-prompt
                        </code>
                      </td>
                      <td className="p-3">No</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Custom prompt text
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          data-inline
                        </code>
                      </td>
                      <td className="p-3">No</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Set to "true" for inline widget (no floating button,
                        form shows directly on page)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          data-container
                        </code>
                      </td>
                      <td className="p-3">No</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        CSS selector for a container element to mount the widget
                        into (e.g. "#feedback"). Implies inline mode.
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          data-context
                        </code>
                      </td>
                      <td className="p-3">No</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        JSON object with custom context metadata (e.g.{" "}
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          {'\'{"page":"pricing","userId":"123"}\''}
                        </code>
                        )
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          data-debug
                        </code>
                      </td>
                      <td className="p-3">No</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Set to "true" for console logging (default: false)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          data-track-impressions
                        </code>
                      </td>
                      <td className="p-3">No</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Set to "false" to disable page view / impression
                        tracking (default: true)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          data-strip-query-params
                        </code>
                      </td>
                      <td className="p-3">No</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Set to "true" to strip query parameters from tracked
                        URLs for privacy (default: false). Only the domain and
                        path are recorded.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Widget Types */}
            <section id="widget-types" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🎨
                </span>
                Widget Types
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Pollenate offers six different ways to ask for feedback. Choose the one that fits what you're trying to learn.
                Not sure which to pick? <strong>Emoji</strong> and <strong>Stars</strong> work great for most situations.
              </p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6 text-center">
                  <div className="text-3xl mb-3">😞 😐 🙂 😄 🤩</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                    Emoji
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    5-point emoji scale for quick sentiment.
                    Best for: getting a fast read on how people feel.
                  </p>
                  <code className="text-xs bg-slate-100 dark:bg-neutral-800 px-2 py-1 rounded mt-2 inline-block">
                    type="emoji"
                  </code>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6 text-center">
                  <div className="text-3xl mb-3">⭐⭐⭐⭐⭐</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                    Stars
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Classic 5-star rating.
                    Best for: product reviews, service quality, anything people are used to rating out of 5.
                  </p>
                  <code className="text-xs bg-slate-100 dark:bg-neutral-800 px-2 py-1 rounded mt-2 inline-block">
                    type="stars"
                  </code>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6 text-center">
                  <div className="text-3xl mb-3">👍 👎</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                    Thumbs
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Simple up/down voting.
                    Best for: "Was this helpful?" questions on docs, articles, or support pages.
                  </p>
                  <code className="text-xs bg-slate-100 dark:bg-neutral-800 px-2 py-1 rounded mt-2 inline-block">
                    type="thumbs"
                  </code>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6 text-center">
                  <div className="text-2xl mb-3 font-mono">0─────10</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                    NPS
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Net Promoter Score (0-10).
                    Best for: measuring loyalty — "How likely are you to recommend us?"
                  </p>
                  <code className="text-xs bg-slate-100 dark:bg-neutral-800 px-2 py-1 rounded mt-2 inline-block">
                    type="nps"
                  </code>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6 text-center">
                  <div className="text-2xl mb-3 font-mono">1─────5</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                    CSAT
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Customer Satisfaction (1-5).
                    Best for: measuring satisfaction after a specific interaction, like a support call or purchase.
                  </p>
                  <code className="text-xs bg-slate-100 dark:bg-neutral-800 px-2 py-1 rounded mt-2 inline-block">
                    type="csat"
                  </code>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6 text-center">
                  <div className="text-3xl mb-3">💬</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                    Text
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Open-ended text feedback.
                    Best for: collecting suggestions, bug reports, or detailed comments in the user's own words.
                  </p>
                  <code className="text-xs bg-slate-100 dark:bg-neutral-800 px-2 py-1 rounded mt-2 inline-block">
                    type="text"
                  </code>
                </div>
              </div>
            </section>

            {/* Widget Customization */}
            <section id="widget-customization" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🎭
                </span>
                Customization
              </h2>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> You can change the colors, fonts, and shape of the widget to match your website's look.
                  If you don't want to touch code, you can create custom themes in the Pollenate dashboard under <em>Brand Settings</em> instead.
                </p>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Customize the widget appearance using CSS variables or create
                custom themes in the dashboard.
              </p>

              <CodeBlock
                language="css"
                label="CSS Variables"
                code={`:root {
  --pollenate-primary: #f59e0b;
  --pollenate-primary-hover: #d97706;
  --pollenate-bg: #ffffff;
  --pollenate-text: #1e293b;
  --pollenate-border: #e2e8f0;
  --pollenate-radius: 12px;
}`}
              />
            </section>

            {/* React Package */}
            <section id="react-package" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  ⚛️
                </span>
                React Package
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                For React apps, install{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-sm">
                  @pollenate/react
                </code>{" "}
                for a fully typed, native React component — no script tags or
                DOM manipulation needed.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Installation
              </h3>
              <CodeBlock
                language="bash"
                label="Terminal"
                className="mb-6"
                code={`npm install @pollenate/react\n# or\npnpm add @pollenate/react\n# or\nyarn add @pollenate/react`}
              />

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Quick Start
              </h3>
              <CodeBlock
                language="tsx"
                label="React Component"
                className="mb-6"
                code={`import { PollenateWidget } from '@pollenate/react';\n\nexport function App() {\n  return (\n    <PollenateWidget\n      inboxKey="YOUR_INBOX_KEY"\n      apiKey="YOUR_API_KEY"\n      type="stars"\n      theme="auto"\n      onSubmit={(event) => {\n        console.log('Feedback submitted:', event.detail);\n      }}\n    />\n  );\n}`}
              />

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Inline Widget
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                Add{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-sm">
                  inline
                </code>{" "}
                to render the widget directly in your page flow instead of as a
                floating button:
              </p>
              <CodeBlock
                language="tsx"
                label="Inline Example"
                className="mb-6"
                code={`<PollenateWidget\n  inboxKey="my-inbox"\n  apiKey="pk_live_xxx"\n  type="thumbs"\n  inline\n  prompt="Was this page helpful?"\n/>`}
              />

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Custom Themed
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                Override colors, border radius, fonts, and dark-mode colors:
              </p>
              <CodeBlock
                language="tsx"
                label="Themed Example"
                className="mb-6"
                code={`<PollenateWidget\n  inboxKey="my-inbox"\n  apiKey="pk_live_xxx"\n  type="nps"\n  theme="auto"\n  primaryColor="#6366f1"\n  borderRadius={12}\n  buttonText="Submit Score"\n  darkPrimaryColor="#818cf8"\n  darkBgColor="#1e1b4b"\n  hideBranding\n/>`}
              />

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Props Reference
              </h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold text-slate-700 dark:text-neutral-300">
                        Prop
                      </th>
                      <th className="text-left p-3 font-semibold text-slate-700 dark:text-neutral-300">
                        Type
                      </th>
                      <th className="text-left p-3 font-semibold text-slate-700 dark:text-neutral-300">
                        Default
                      </th>
                      <th className="text-left p-3 font-semibold text-slate-700 dark:text-neutral-300">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-700">
                    <tr>
                      <td className="p-3 font-mono text-xs text-amber-600 dark:text-amber-400">
                        inboxKey
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        string
                      </td>
                      <td className="p-3 text-slate-500 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Your inbox key (required)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs text-amber-600 dark:text-amber-400">
                        apiKey
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        string
                      </td>
                      <td className="p-3 text-slate-500 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Your API key with collect scope (required)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs text-amber-600 dark:text-amber-400">
                        type
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        string
                      </td>
                      <td className="p-3 text-slate-500 dark:text-neutral-400">
                        emoji
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        emoji, stars, thumbs, nps, csat, or text
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs text-amber-600 dark:text-amber-400">
                        theme
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        string
                      </td>
                      <td className="p-3 text-slate-500 dark:text-neutral-400">
                        light
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        light, dark, or auto
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs text-amber-600 dark:text-amber-400">
                        inline
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        boolean
                      </td>
                      <td className="p-3 text-slate-500 dark:text-neutral-400">
                        false
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Render inline instead of floating
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs text-amber-600 dark:text-amber-400">
                        position
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        string
                      </td>
                      <td className="p-3 text-slate-500 dark:text-neutral-400">
                        bottom-right
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Floating position (bottom-right, bottom-left, top-right,
                        top-left)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs text-amber-600 dark:text-amber-400">
                        prompt
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        string
                      </td>
                      <td className="p-3 text-slate-500 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Custom prompt text above the rating
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs text-amber-600 dark:text-amber-400">
                        context
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        object
                      </td>
                      <td className="p-3 text-slate-500 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Metadata sent with each submission
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs text-amber-600 dark:text-amber-400">
                        primaryColor
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        string
                      </td>
                      <td className="p-3 text-slate-500 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Primary accent color (hex)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs text-amber-600 dark:text-amber-400">
                        bgColor
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        string
                      </td>
                      <td className="p-3 text-slate-500 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Widget background color (hex)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs text-amber-600 dark:text-amber-400">
                        onSubmit
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        function
                      </td>
                      <td className="p-3 text-slate-500 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Callback when feedback is submitted
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs text-amber-600 dark:text-amber-400">
                        onImpression
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        function
                      </td>
                      <td className="p-3 text-slate-500 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Callback on widget impression
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-slate-100 dark:bg-neutral-800/50 rounded-xl p-4">
                <p className="text-slate-600 dark:text-neutral-400 text-sm">
                  <strong>📦 Full props list:</strong> See all available props
                  including dark-mode overrides, animation, and branding options
                  in the{" "}
                  <a
                    href="https://www.npmjs.com/package/@pollenate/react"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-amber-600 dark:text-amber-400 underline"
                  >
                    npm package README
                  </a>
                  .
                </p>
              </div>
            </section>

            {/* Custom Widgets */}
            <section id="custom-widgets" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🛠️
                </span>
                Custom Widgets
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Want complete control over your feedback UI? Build your own
                custom widget using our REST API. This allows you to match your
                brand perfectly and integrate feedback into any user flow. If
                you're using React, consider the{" "}
                <a
                  href="#react-package"
                  className="text-amber-600 dark:text-amber-400 underline"
                >
                  @pollenate/react package
                </a>{" "}
                first — it handles all the API calls and UI for you.
              </p>

              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/40 rounded-xl p-4 mb-6">
                <p className="text-amber-800 dark:text-amber-300 text-sm">
                  <strong>💡 When to use custom widgets:</strong> Use the API
                  directly when you need a fully custom UI, want to embed
                  feedback in specific flows (like checkout), or integrate with
                  frameworks we don't support yet.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Basic Example
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                Here's a complete example of a custom feedback form using
                vanilla HTML/JavaScript:
              </p>

              <CodeBlock
                language="html"
                label="HTML + JavaScript"
                className="mb-6"
                code={`<form id="custom-feedback">
  <p>How would you rate your experience?</p>
  <div class="rating-buttons">
    <button type="button" data-score="1">😞</button>
    <button type="button" data-score="2">😐</button>
    <button type="button" data-score="3">🙂</button>
    <button type="button" data-score="4">😄</button>
    <button type="button" data-score="5">🤩</button>
  </div>
  <textarea name="comment" placeholder="Any additional feedback?"></textarea>
  <button type="submit">Submit</button>
</form>

<script>
let selectedScore = null;

document.querySelectorAll('[data-score]').forEach(btn => {
  btn.onclick = () => selectedScore = parseInt(btn.dataset.score);
});

document.getElementById('custom-feedback').onsubmit = async (e) => {
  e.preventDefault();
  
  const response = await fetch('https://api.pollenate.dev/collect', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Pollenate-Key': 'YOUR_API_KEY'
    },
    body: JSON.stringify({
      inboxKey: 'YOUR_INBOX_KEY',
      type: 'emoji',
      score: selectedScore,
      comment: e.target.comment.value,
      context: {
        page: window.location.pathname,
        userAgent: navigator.userAgent
      }
    })
  });
  
  if (response.ok) {
    alert('Thank you for your feedback!');
  }
};
</script>`}
              />

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                React Example (DIY)
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                This shows how to build a completely custom feedback UI from
                scratch using the API directly. For a drop-in solution, see the{" "}
                <a
                  href="#react-package"
                  className="text-amber-600 dark:text-amber-400 underline"
                >
                  React Package
                </a>{" "}
                section above.
              </p>
              <CodeBlock
                language="tsx"
                label="Custom React Component"
                className="mb-6"
                code={`import { useState } from 'react';

export function CustomFeedback() {
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    const response = await fetch('https://api.pollenate.dev/collect', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Pollenate-Key': process.env.POLLENATE_API_KEY
      },
      body: JSON.stringify({
        inboxKey: 'YOUR_INBOX_KEY',
        type: 'emoji',
        score,
        comment,
        context: { page: window.location.pathname }
      })
    });
    
    if (response.ok) setSubmitted(true);
  };

  if (submitted) return <p>Thanks for your feedback! 🎉</p>;

  return (
    <div className="feedback-widget">
      <p>How was your experience?</p>
      <div className="emojis">
        {['😞', '😐', '🙂', '😄', '🤩'].map((emoji, i) => (
          <button
            key={i}
            onClick={() => setScore(i + 1)}
            className={score === i + 1 ? 'selected' : ''}
          >
            {emoji}
          </button>
        ))}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Tell us more..."
      />
      <button onClick={handleSubmit} disabled={!score}>
        Submit Feedback
      </button>
    </div>
  );
}`}
              />

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                API Request Format
              </h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">Field</th>
                      <th className="text-left p-3 font-semibold">Type</th>
                      <th className="text-left p-3 font-semibold">Required</th>
                      <th className="text-left p-3 font-semibold">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          inboxKey
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        string
                      </td>
                      <td className="p-3">Yes</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Your inbox's public key
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          type
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        string
                      </td>
                      <td className="p-3">Yes</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        emoji, stars, thumbs, nps, csat, or text
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          score
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        number
                      </td>
                      <td className="p-3">No*</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        0-10 for NPS, 1-5 for others, 0/1 for thumbs
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          comment
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        string
                      </td>
                      <td className="p-3">No*</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Text feedback (max 2000 chars)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          context
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        object
                      </td>
                      <td className="p-3">No</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Custom metadata (page URL, user ID, etc.)
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-xs text-slate-500 dark:text-neutral-400 mt-2">
                  * At least one of score or comment is required
                </p>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Score Ranges by Type
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-lg p-4">
                  <code className="text-amber-600 dark:text-amber-400 font-semibold">
                    emoji
                  </code>
                  <p className="text-sm text-slate-600 dark:text-neutral-400 mt-1">
                    Score 1-5 (😞 to 🤩)
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-lg p-4">
                  <code className="text-amber-600 dark:text-amber-400 font-semibold">
                    stars
                  </code>
                  <p className="text-sm text-slate-600 dark:text-neutral-400 mt-1">
                    Score 1-5 (⭐ to ⭐⭐⭐⭐⭐)
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-lg p-4">
                  <code className="text-amber-600 dark:text-amber-400 font-semibold">
                    thumbs
                  </code>
                  <p className="text-sm text-slate-600 dark:text-neutral-400 mt-1">
                    Score 0 (👎) or 1 (👍)
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-lg p-4">
                  <code className="text-amber-600 dark:text-amber-400 font-semibold">
                    nps
                  </code>
                  <p className="text-sm text-slate-600 dark:text-neutral-400 mt-1">
                    Score 0-10
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-lg p-4">
                  <code className="text-amber-600 dark:text-amber-400 font-semibold">
                    csat
                  </code>
                  <p className="text-sm text-slate-600 dark:text-neutral-400 mt-1">
                    Score 1-5
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-lg p-4">
                  <code className="text-amber-600 dark:text-amber-400 font-semibold">
                    text
                  </code>
                  <p className="text-sm text-slate-600 dark:text-neutral-400 mt-1">
                    No score, comment only
                  </p>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Using Context for Insights
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                The{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                  context
                </code>{" "}
                field lets you attach metadata to each feedback submission. This
                enables powerful filtering and analysis in your dashboard.
              </p>
              <CodeBlock
                language="json"
                className="mb-6"
                code={`// Example context object
{
  "context": {
    "page": "/checkout",
    "userId": "user_abc123",
    "plan": "pro",
    "feature": "dark-mode",
    "experiment": "new-pricing-v2",
    "sessionId": "sess_xyz789"
  }
}`}
              />
            </section>

            {/* Feedback Pages Overview */}
            <section id="feedback-pages" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  📋
                </span>
                Feedback Pages
              </h2>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> Feedback Pages are like online surveys. You build a form with questions,
                  and Pollenate gives you a link you can share anywhere — by email, text message,
                  on social media, or even printed as a QR code on a poster or business card.
                  No website or coding required. Respondents just open the link and fill it out.
                </p>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Feedback Pages are standalone, branded feedback forms that you
                create through the Pollenate dashboard. Unlike widgets (which
                embed into existing pages), Feedback Pages are hosted at their
                own URL and can collect multi-question surveys from users.
              </p>

              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/40 rounded-xl p-4 mb-6">
                <p className="text-amber-800 dark:text-amber-300 text-sm">
                  <strong>💡 When to use Feedback Pages vs. Widgets:</strong>{" "}
                  Use <strong>widgets</strong> when you want to collect quick,
                  single-question feedback inline on your existing website. Use{" "}
                  <strong>Feedback Pages</strong> when you need a multi-question
                  form, want to share via QR code or printed materials, or need
                  a standalone survey that doesn't require embedding on an
                  existing site.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Key Features
              </h3>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-1">
                    🛠️ Visual Form Builder
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Drag-and-drop editor with 7+ question types. Add, edit,
                    delete, and reorder questions with a live preview.
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-1">
                    🎨 Custom Branding
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Match your brand with custom accent colors, background
                    colors, and brand association. Configurable thank-you
                    messages.
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-1">
                    📱 Multiple Sharing Options
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Share via direct link, QR code (PNG/SVG), email signature
                    button, embeddable iframe, or print-ready flyer.
                  </p>
                </div>
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-1">
                    📊 Built-in Analytics
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    View response counts, page views, conversion rates, daily
                    trends, and per-question breakdowns with score
                    distributions.
                  </p>
                </div>
              </div>
            </section>

            {/* Feedback Pages Form Builder */}
            <section id="feedback-pages-builder" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🔨
                </span>
                Form Builder
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                The form builder lets you create multi-question feedback forms
                using a visual editor. Each question has a type, prompt,
                optional description, and can be marked as required.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Supported Question Types
              </h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">Type</th>
                      <th className="text-left p-3 font-semibold">API Value</th>
                      <th className="text-left p-3 font-semibold">
                        Score Range
                      </th>
                      <th className="text-left p-3 font-semibold">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    <tr>
                      <td className="p-3">⭐ Star Rating</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          stars
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        1–5
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Classic 5-star rating
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">😄 Emoji</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          emoji
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        1–5
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        5-point emoji sentiment scale
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">👍 Thumbs</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          thumbs
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        0–1
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Simple thumbs up/down
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">📊 NPS</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          nps
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        0–10
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Net Promoter Score
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">😊 CSAT</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          csat
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        1–5
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Customer Satisfaction score
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">💬 Text</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          text
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Open-ended text response (single-line or multi-line, with optional validation)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">🔘 Single Choice</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          single_choice
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Select one option from a list
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">☑️ Multiple Choice</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          multi_choice
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Select multiple options from a list
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">📧 Email</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          email
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Email address input with built-in format validation
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">📞 Phone</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          phone
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Phone number with automatic formatting (US, international, or any)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3"># Number</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          number
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Numeric input with optional min/max, step, and unit label
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">📅 Date</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          date
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Date picker with optional time component
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">▾ Dropdown</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          dropdown
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Single selection from a dropdown list (compact for 5+ options)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">↕️ Rank Order</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          ranking
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Drag-and-drop to rank a list of options in preferred order
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">📎 File Upload</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          file_upload
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Respondents attach files (images, documents, audio, or video); set allowed types, max files, and size limit
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">🎤 Voice Recording</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          voice
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Respondents record an audio message in the browser; set a max recording length
                      </td>
                    </tr>
                    <tr className="bg-slate-50 dark:bg-neutral-800/50">
                      <td className="p-3">📝 Content Block</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          content_block
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        <span className="italic text-xs">display only</span>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Rich markdown content between questions (instructions, context)
                      </td>
                    </tr>
                    <tr className="bg-slate-50 dark:bg-neutral-800/50">
                      <td className="p-3">— Section Header</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          section_header
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        <span className="italic text-xs">display only</span>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Section heading and divider to organize the form
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Page Settings
              </h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">Setting</th>
                      <th className="text-left p-3 font-semibold">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    <tr>
                      <td className="p-3 font-medium text-slate-900 dark:text-white">
                        Title &amp; Description
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Shown at the top of the public feedback page
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium text-slate-900 dark:text-white">
                        URL Slug
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Custom URL path (e.g.{" "}
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          /f/your-org/my-survey
                        </code>
                        )
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium text-slate-900 dark:text-white">
                        Status
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Draft (private), Published (live), or Archived (closed)
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium text-slate-900 dark:text-white">
                        Accent &amp; Background Color
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Customize the page's accent and background colors to
                        match your brand
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium text-slate-900 dark:text-white">
                        Thank You Message
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Custom message shown after submission, with optional
                        redirect URL
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium text-slate-900 dark:text-white">
                        Require Contact Info
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Ask respondents for name and email before submitting
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium text-slate-900 dark:text-white">
                        Allow Anonymous
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Let respondents skip contact info even if the field is
                        shown
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium text-slate-900 dark:text-white">
                        Single Submission
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Limit to one response per browser using local storage
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Import from Google Forms */}
            <section id="feedback-pages-google-forms" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  📋
                </span>
                Import from Google Forms
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Already have a Google Form? You can migrate it to a Pollenate
                feedback page in a couple of clicks — no Apps Script, no OAuth,
                and no AI calls. Pollenate fetches the public form, parses its
                questions, and creates a matching feedback page for you to
                review and edit.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Steps
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-slate-600 dark:text-neutral-400 mb-6">
                <li>
                  In Google Forms, make sure the form is shared with{" "}
                  <strong>Anyone with the link</strong> (Settings → Responses →
                  toggle off "Restrict to users").
                </li>
                <li>
                  Copy the form's URL. Both{" "}
                  <code className="bg-slate-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-xs">
                    docs.google.com/forms/...
                  </code>{" "}
                  and{" "}
                  <code className="bg-slate-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-xs">
                    forms.gle/...
                  </code>{" "}
                  short links work. Editor URLs (
                  <code className="bg-slate-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-xs">
                    /edit
                  </code>
                  ) are normalized to{" "}
                  <code className="bg-slate-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded text-xs">
                    /viewform
                  </code>{" "}
                  automatically.
                </li>
                <li>
                  In Pollenate, open <strong>Feedback Pages</strong> → click{" "}
                  <strong>+ New Page</strong> → choose the{" "}
                  <strong>Google Forms</strong> tab.
                </li>
                <li>
                  Paste the URL and click <strong>Import</strong>. You'll see a
                  preview of the parsed title, description, and questions.
                </li>
                <li>
                  Click <strong>Create Page</strong> to save. The new page opens
                  in the editor where you can tweak anything before publishing.
                </li>
              </ol>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Question Type Mapping
              </h3>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">
                        Google Forms
                      </th>
                      <th className="text-left p-3 font-semibold">Pollenate</th>
                      <th className="text-left p-3 font-semibold">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    <tr>
                      <td className="p-3 font-mono text-xs">SHORT_ANSWER</td>
                      <td className="p-3">text (single-line)</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">PARAGRAPH</td>
                      <td className="p-3">text (multi-line)</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">MULTIPLE_CHOICE</td>
                      <td className="p-3">single_choice</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Options preserved
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">CHECKBOXES</td>
                      <td className="p-3">multi_choice</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Options preserved
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">DROPDOWN</td>
                      <td className="p-3">dropdown</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Options preserved
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">
                        LINEAR_SCALE (1–5)
                      </td>
                      <td className="p-3">stars</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">
                        LINEAR_SCALE (0–10)
                      </td>
                      <td className="p-3">nps</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">
                        LINEAR_SCALE (other)
                      </td>
                      <td className="p-3">single_choice</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Numeric options
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">DATE</td>
                      <td className="p-3">date</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">
                        SECTION_HEADER / PAGE_BREAK
                      </td>
                      <td className="p-3">section_header</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        —
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">IMAGE / VIDEO</td>
                      <td className="p-3">content_block</td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Caption text imported; media must be re-added
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-mono text-xs">
                        GRID / TIME / FILE_UPLOAD
                      </td>
                      <td className="p-3 italic text-slate-500 dark:text-neutral-400">
                        Not imported
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Surfaced in a warning so you can re-add manually
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                What Doesn't Carry Over
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                The public Google Forms payload doesn't expose a handful of
                editor-only settings, so these aren't imported:
              </p>
              <ul className="list-disc list-inside space-y-1 text-slate-600 dark:text-neutral-400 mb-6">
                <li>Confirmation / "form has been submitted" message</li>
                <li>"Collect email addresses" setting</li>
                <li>"Limit to one response per user" setting</li>
                <li>Grid, time, and file-upload question types</li>
                <li>Images and videos (caption text is imported)</li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Troubleshooting
              </h3>
              <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-neutral-400 mb-6">
                <li>
                  <strong>"Form is not publicly accessible"</strong> — open the
                  Google Form's sharing settings and make sure it's set to{" "}
                  <em>Anyone with the link</em>. Forms restricted to a Google
                  Workspace can't be imported.
                </li>
                <li>
                  <strong>"Could not find form data"</strong> — Google
                  occasionally changes the embedded payload format. Try
                  re-saving the form in Google Forms, or copy the questions
                  manually using the form builder.
                </li>
                <li>
                  <strong>Some questions are missing</strong> — check the import
                  warning for the list of unsupported types (grid, time, file
                  upload). Re-add them manually after importing.
                </li>
              </ul>
            </section>

            {/* Feedback Pages Sharing */}
            <section id="feedback-pages-sharing" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🔗
                </span>
                Sharing &amp; Embedding
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Once your Feedback Page is published, you have multiple ways to
                share it with respondents.
              </p>

              <div className="space-y-6">
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                    🔗 Direct Link
                  </h3>
                  <p className="text-slate-600 dark:text-neutral-400 text-sm mb-3">
                    Share the URL directly via email, messaging apps, or social
                    media.
                  </p>
                  <CodeBlock
                    language="text"
                    theme="auto"
                    code="https://pollenate.dev/f/your-org/your-page-slug"
                  />
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                    📱 QR Code
                  </h3>
                  <p className="text-slate-600 dark:text-neutral-400 text-sm mb-3">
                    Generate QR codes in PNG or SVG format at multiple sizes
                    (128px to 1024px). Perfect for flyers, posters, table tents,
                    and business cards.
                  </p>
                  <CodeBlock
                    language="text"
                    theme="auto"
                    code={`GET https://api.pollenate.dev/qr?data=YOUR_PAGE_URL&size=256&format=png&margin=4

Parameters:
  data    - URL to encode (required)
  size    - Image size in pixels: 128, 256, 512, 1024 (default: 256)
  format  - Output format: png or svg (default: png)
  margin  - Quiet zone margin in modules (default: 4)`}
                  />
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                    📧 Email Signature Button
                  </h3>
                  <p className="text-slate-600 dark:text-neutral-400 text-sm mb-3">
                    Copy an HTML snippet that renders a branded button in your
                    email signature. The button uses your page's accent color.
                  </p>
                  <CodeBlock
                    language="html"
                    theme="auto"
                    code={`<table cellpadding="0" cellspacing="0" border="0">
  <tr>
    <td style="padding: 10px 0;">
      <a href="https://pollenate.dev/f/your-org/your-page"
         style="display: inline-block; padding: 8px 16px;
                background-color: #f59e0b; color: #ffffff;
                text-decoration: none; border-radius: 6px;
                font-family: Arial, sans-serif; font-size: 14px;">
        Share Your Feedback
      </a>
    </td>
  </tr>
</table>`}
                  />
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                    🖥️ Embed on Website
                  </h3>
                  <p className="text-slate-600 dark:text-neutral-400 text-sm mb-3">
                    Embed the full feedback page on your website using an
                    iframe. Add{" "}
                    <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                      ?embed=true
                    </code>{" "}
                    to remove the header and footer.
                  </p>
                  <CodeBlock
                    language="html"
                    theme="auto"
                    code={`<iframe
  src="https://pollenate.dev/f/your-org/your-page?embed=true"
  width="100%"
  height="600"
  frameborder="0"
  style="border: none; border-radius: 12px;"
  title="Feedback Form"
></iframe>`}
                  />
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                    🖨️ Print Flyer
                  </h3>
                  <p className="text-slate-600 dark:text-neutral-400 text-sm">
                    Generate a ready-to-print flyer with your page title,
                    description, QR code, and a call-to-action. Uses the Print
                    dialog for easy printing on any device. Great for clinics,
                    restaurants, offices, and events.
                  </p>
                </div>
              </div>
            </section>

            {/* Feedback Pages API */}
            <section id="feedback-pages-api" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  ⚙️
                </span>
                Pages API
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Manage Feedback Pages programmatically via the REST API. All
                endpoints require JWT Bearer token authentication.
              </p>

              <div className="space-y-4 mb-6">
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden">
                  <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                    <span className="bg-blue-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                      GET
                    </span>
                    <code className="text-slate-900 dark:text-white break-all">
                      /organizations/:orgId/feedback-pages
                    </code>
                  </div>
                  <div className="p-4">
                    <p className="text-slate-600 dark:text-neutral-400 text-sm">
                      List all feedback pages. Supports filtering by{" "}
                      <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                        status
                      </code>{" "}
                      and{" "}
                      <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                        brandId
                      </code>
                      .
                    </p>
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden">
                  <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                    <span className="bg-green-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                      POST
                    </span>
                    <code className="text-slate-900 dark:text-white break-all">
                      /organizations/:orgId/feedback-pages
                    </code>
                  </div>
                  <div className="p-4">
                    <p className="text-slate-600 dark:text-neutral-400 text-sm">
                      Create a new feedback page with title, description, slug,
                      status, and settings.
                    </p>
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden">
                  <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                    <span className="bg-blue-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                      GET
                    </span>
                    <code className="text-slate-900 dark:text-white break-all">
                      /organizations/:orgId/feedback-pages/:pageId
                    </code>
                  </div>
                  <div className="p-4">
                    <p className="text-slate-600 dark:text-neutral-400 text-sm">
                      Get a feedback page with its questions and submission
                      count.
                    </p>
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden">
                  <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                    <span className="bg-amber-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                      PUT
                    </span>
                    <code className="text-slate-900 dark:text-white break-all">
                      /organizations/:orgId/feedback-pages/:pageId
                    </code>
                  </div>
                  <div className="p-4">
                    <p className="text-slate-600 dark:text-neutral-400 text-sm">
                      Update page settings (title, description, status, colors,
                      submission settings, etc.).
                    </p>
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden">
                  <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                    <span className="bg-green-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                      POST
                    </span>
                    <code className="text-slate-900 dark:text-white break-all">
                      /organizations/:orgId/feedback-pages/:pageId/questions
                    </code>
                  </div>
                  <div className="p-4">
                    <p className="text-slate-600 dark:text-neutral-400 text-sm">
                      Add a question to the page.
                    </p>
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden">
                  <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                    <span className="bg-blue-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                      GET
                    </span>
                    <code className="text-slate-900 dark:text-white break-all">
                      /organizations/:orgId/feedback-pages/:pageId/stats
                    </code>
                  </div>
                  <div className="p-4">
                    <p className="text-slate-600 dark:text-neutral-400 text-sm">
                      Get analytics: total responses, page views, conversion
                      rate, daily trends, and per-question breakdowns.
                    </p>
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden">
                  <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                    <span className="bg-blue-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                      GET
                    </span>
                    <code className="text-slate-900 dark:text-white break-all">
                      /f/:orgSlug/:pageSlug
                    </code>
                  </div>
                  <div className="p-4">
                    <p className="text-slate-600 dark:text-neutral-400 text-sm">
                      Public-facing feedback page (no auth required). Append{" "}
                      <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                        ?embed=true
                      </code>{" "}
                      for iframe mode.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Submit Responses (Public)
              </h3>
              <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden mb-6">
                <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                  <span className="bg-green-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                    POST
                  </span>
                  <code className="text-slate-900 dark:text-white break-all">
                    /f/:orgSlug/:pageSlug/submit
                  </code>
                </div>
                <div className="p-4">
                  <p className="text-slate-600 dark:text-neutral-400 text-sm mb-4">
                    Submit responses to a published feedback page. Protected by
                    Cloudflare Turnstile.
                  </p>
                  <CodeBlock
                    language="json"
                    theme="auto"
                    code={`{
  "answers": {
    "question-id-1": { "score": 5 },
    "question-id-2": { "textValue": "Great experience!" },
    "question-id-3": { "multiValues": ["Search", "Referral"] }
  },
  "respondentName": "Jane Doe",
  "respondentEmail": "jane@example.com",
  "turnstileToken": "CAPTCHA_TOKEN"
}`}
                  />
                </div>
              </div>
            </section>

            {/* API Overview */}
            <section id="api-overview" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  ⚡
                </span>
                API Overview
              </h2>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> The API is a way for other software (your app, your website's code, or third-party
                  tools) to talk to Pollenate directly — sending feedback, pulling reports, or managing inboxes
                  without going through the dashboard. This section is primarily for developers.
                </p>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                The Pollenate API is a RESTful API that uses JSON for request
                and response bodies.
              </p>

              <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden mb-6">
                <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800">
                  <span className="font-semibold text-slate-900 dark:text-white">
                    Base URLs
                  </span>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-400 px-2 py-0.5 rounded text-xs font-mono">
                      PROD
                    </span>
                    <code className="text-slate-600 dark:text-neutral-400">
                      https://api.pollenate.dev
                    </code>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="bg-slate-200 dark:bg-neutral-700 text-slate-600 dark:text-neutral-300 px-2 py-0.5 rounded text-xs font-mono">
                      DEV
                    </span>
                    <code className="text-slate-600 dark:text-neutral-400">
                      http://localhost:8787
                    </code>
                  </div>
                </div>
              </div>

              <p className="text-slate-600 dark:text-neutral-400">
                For the complete API specification, see our{" "}
                <a
                  href="/openapi.yaml"
                  target="_blank"
                  className="text-amber-600 dark:text-amber-400 hover:text-amber-700 underline"
                >
                  OpenAPI 3.1 spec
                </a>
                .
              </p>
            </section>

            {/* API Feedback */}
            <section id="api-feedback" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  📥
                </span>
                Feedback Collection
              </h2>

              <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden mb-6">
                <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                  <span className="bg-green-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                    POST
                  </span>
                  <code className="text-slate-900 dark:text-white break-all">
                    /collect
                  </code>
                </div>
                <div className="p-4">
                  <p className="text-slate-600 dark:text-neutral-400 text-sm mb-4">
                    Submit feedback to an inbox. Requires API key
                    authentication.
                  </p>

                  <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                    Request Body
                  </h4>
                  <CodeBlock
                    language="json"
                    theme="auto"
                    className="mb-4"
                    code={`{
  "type": "emoji",        // emoji, stars, thumbs, nps, csat, text
  "score": 5,             // 0-10 for nps, 1-5 for others
  "comment": "Loving it!",// Optional text feedback
  "context": {            // Optional metadata
    "page": "/checkout",
    "userId": "user_123"
  }
}`}
                  />

                  <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                    Response
                  </h4>
                  <CodeBlock
                    language="json"
                    theme="auto"
                    code={`{
  "id": "fb_abc123",
  "createdAt": "2026-02-04T12:00:00Z"
}`}
                  />
                </div>
              </div>
            </section>

            {/* API Inboxes */}
            <section id="api-inboxes" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  📬
                </span>
                Inboxes
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                Inboxes are containers for feedback. Each inbox has a unique key
                used to submit feedback.
              </p>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> An inbox is like a labeled folder where feedback lands.
                  You might have an inbox called "Website" for your homepage widget and another called "Post-Visit" for patient surveys.
                  When you set up a widget or survey, you tell it which inbox to deliver feedback to using the inbox's unique key.
                </p>
              </div>

              <div className="space-y-4">
                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden">
                  <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                    <span className="bg-blue-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                      GET
                    </span>
                    <code className="text-slate-900 dark:text-white break-all">
                      /organizations/:orgId/inboxes
                    </code>
                  </div>
                  <div className="p-4">
                    <p className="text-slate-600 dark:text-neutral-400 text-sm">
                      List all inboxes in an organization.
                    </p>
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden">
                  <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                    <span className="bg-green-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                      POST
                    </span>
                    <code className="text-slate-900 dark:text-white break-all">
                      /organizations/:orgId/inboxes
                    </code>
                  </div>
                  <div className="p-4">
                    <p className="text-slate-600 dark:text-neutral-400 text-sm">
                      Create a new inbox.
                    </p>
                  </div>
                </div>

                <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden">
                  <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                    <span className="bg-blue-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                      GET
                    </span>
                    <code className="text-slate-900 dark:text-white break-all">
                      /organizations/:orgId/inboxes/:inboxId/feedback
                    </code>
                  </div>
                  <div className="p-4">
                    <p className="text-slate-600 dark:text-neutral-400 text-sm">
                      Get all feedback for an inbox with pagination and filters.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* API Search */}
            <section id="api-search" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🔍
                </span>
                Semantic Search
              </h2>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> Instead of searching for an exact word, you can describe what you're looking for in everyday language
                  — like "people unhappy with wait times" — and Pollenate's AI will find the most relevant feedback, even if those exact words
                  weren't used. You can use this in the dashboard's search bar too, not just the API.
                </p>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Search feedback using natural language. Powered by AI embeddings
                for semantic understanding.
              </p>

              <div className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden mb-6">
                <div className="bg-slate-100 dark:bg-neutral-800 px-4 py-3 border-b border-slate-200 dark:border-neutral-800 flex items-center gap-2 min-w-0">
                  <span className="bg-green-700 text-white px-2 py-0.5 rounded text-xs font-mono font-semibold shrink-0">
                    POST
                  </span>
                  <code className="text-slate-900 dark:text-white break-all">
                    /organizations/:orgId/search
                  </code>
                </div>
                <div className="p-4">
                  <CodeBlock
                    language="json"
                    theme="auto"
                    code={`{
  "query": "users frustrated with checkout",
  "limit": 20,
  "inboxId": "inbox_123",
  "brandId": "brand_456"
}`}
                  />
                </div>
              </div>
            </section>

            {/* AI Agents */}
            <section id="ai-agents" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🤖
                </span>
                AI Agent Integration
              </h2>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> If you use AI chatbots (like a support bot on your website),
                  they can automatically send feedback to Pollenate when a user expresses happiness or frustration.
                  This means you capture real sentiment without asking the user to fill out a separate survey.
                </p>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                AI agents can submit feedback during conversations with users —
                capturing satisfaction, sentiment, and product feedback in
                real-time without interrupting the flow.
              </p>

              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/40 rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-amber-900 dark:text-amber-300 mb-2">
                  How it works
                </h3>
                <p className="text-amber-800 dark:text-amber-300 text-sm mb-4">
                  When a user expresses satisfaction or frustration during a
                  conversation, the AI agent can submit structured feedback to
                  Pollenate using the same{" "}
                  <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                    /collect
                  </code>{" "}
                  endpoint used by widgets. The feedback appears in your
                  dashboard alongside widget submissions.
                </p>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm text-amber-800 dark:text-amber-300">
                  <span className="bg-amber-200 dark:bg-amber-800/50 rounded-full px-3 py-1">
                    User talks to AI
                  </span>
                  <span>→</span>
                  <span className="bg-amber-200 dark:bg-amber-800/50 rounded-full px-3 py-1">
                    Agent detects sentiment
                  </span>
                  <span>→</span>
                  <span className="bg-amber-200 dark:bg-amber-800/50 rounded-full px-3 py-1">
                    POST /collect
                  </span>
                  <span>→</span>
                  <span className="bg-amber-200 dark:bg-amber-800/50 rounded-full px-3 py-1">
                    Dashboard
                  </span>
                </div>
              </div>

              <div id="ai-skill-file" className="mb-8">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                  Skill File Discovery
                </h3>
                <p className="text-slate-600 dark:text-neutral-400 mb-4">
                  Pollenate publishes a <strong>skill.md</strong> file that AI
                  agents and platforms can use to discover and learn the API.
                  For deeper integration, see the <a href="#ai-mcp-server" className="text-amber-600 dark:text-amber-400 underline">MCP Server</a> section below.
                </p>
                <div className="grid sm:grid-cols-3 gap-4 mb-4">
                  <a
                    href="/skill.md"
                    target="_blank"
                    className="block bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-lg p-4 hover:border-amber-300 dark:hover:border-amber-700 transition-colors"
                  >
                    <div className="font-mono text-sm text-amber-600 dark:text-amber-400 mb-1">
                      skill.md
                    </div>
                    <div className="text-slate-600 dark:text-neutral-400 text-sm">
                      Human-readable instructions for AI agents
                    </div>
                  </a>
                  <a
                    href="/skill.json"
                    target="_blank"
                    className="block bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-lg p-4 hover:border-amber-300 dark:hover:border-amber-700 transition-colors"
                  >
                    <div className="font-mono text-sm text-amber-600 dark:text-amber-400 mb-1">
                      skill.json
                    </div>
                    <div className="text-slate-600 dark:text-neutral-400 text-sm">
                      Machine-readable metadata and endpoint info
                    </div>
                  </a>
                  <a
                    href="/openapi.yaml"
                    target="_blank"
                    className="block bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-lg p-4 hover:border-amber-300 dark:hover:border-amber-700 transition-colors"
                  >
                    <div className="font-mono text-sm text-amber-600 dark:text-amber-400 mb-1">
                      openapi.yaml
                    </div>
                    <div className="text-slate-600 dark:text-neutral-400 text-sm">
                      Full OpenAPI 3.1 specification
                    </div>
                  </a>
                </div>
                <CodeBlock
                  language="bash"
                  theme="auto"
                  code={`# Install the skill locally for your AI agent
mkdir -p ~/.agent/skills/pollenate
curl -s https://pollenate.dev/skill.md > ~/.agent/skills/pollenate/SKILL.md
curl -s https://pollenate.dev/skill.json > ~/.agent/skills/pollenate/skill.json`}
                />
              </div>

              <div id="ai-mcp-server" className="mb-8">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                  MCP Server (Model Context Protocol)
                </h3>
                <p className="text-slate-600 dark:text-neutral-400 mb-4">
                  Pollenate provides a built-in <strong>MCP server</strong> that allows AI agents and tools like
                  Claude Desktop, Cursor, Windsurf, and other MCP-compatible clients to interact with your feedback
                  data natively — no custom HTTP calls required.
                </p>
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/40 rounded-xl p-4 mb-6">
                  <p className="text-amber-800 dark:text-amber-300 text-sm">
                    <strong>Endpoint:</strong>{" "}
                    <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                      https://api.pollenate.dev/mcp
                    </code>{" "}
                    — Uses the <a href="https://modelcontextprotocol.io/specification/2025-03-26/basic/transports#streamable-http" target="_blank" rel="noopener noreferrer" className="underline">Streamable HTTP transport</a> (MCP spec 2025-03-26).
                  </p>
                </div>

                <h4 className="font-semibold text-slate-800 dark:text-neutral-200 mb-3">
                  Available Tools
                </h4>
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {[
                    { name: 'submit_feedback', scope: 'collect / write', desc: 'Submit a feedback event to an inbox' },
                    { name: 'list_inboxes', scope: 'read / write / collect', desc: 'List all inboxes for the organization' },
                    { name: 'list_brands', scope: 'read / write', desc: 'List brands (sub-organizations)' },
                    { name: 'get_feedback', scope: 'read / write', desc: 'List and filter feedback events with pagination' },
                    { name: 'get_feedback_stats', scope: 'read / write', desc: 'Aggregated stats and NPS/CSAT scores' },
                    { name: 'search_feedback', scope: 'read / write', desc: 'Full-text search across feedback comments' },
                  ].map((tool) => (
                    <div key={tool.name} className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-lg p-3">
                      <div className="font-mono text-sm text-amber-600 dark:text-amber-400 mb-0.5">{tool.name}</div>
                      <div className="text-slate-600 dark:text-neutral-400 text-sm">{tool.desc}</div>
                      <div className="text-slate-400 dark:text-neutral-400 text-xs mt-1">Scope: {tool.scope}</div>
                    </div>
                  ))}
                </div>

                <h4 className="font-semibold text-slate-800 dark:text-neutral-200 mb-2">
                  Claude Desktop Configuration
                </h4>
                <p className="text-slate-600 dark:text-neutral-400 text-sm mb-3">
                  Add this to your <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">claude_desktop_config.json</code>:
                </p>
                <CodeBlock
                  language="json"
                  theme="auto"
                  code={`{\n  "mcpServers": {\n    "pollenate": {\n      "type": "streamableHttp",\n      "url": "https://api.pollenate.dev/mcp",\n      "headers": {\n        "Authorization": "Bearer pk_live_YOUR_API_KEY"\n      }\n    }\n  }\n}`}
                />

                <h4 className="font-semibold text-slate-800 dark:text-neutral-200 mb-2 mt-6">
                  Cursor / VS Code Configuration
                </h4>
                <p className="text-slate-600 dark:text-neutral-400 text-sm mb-3">
                  Add this to your <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">.cursor/mcp.json</code> or VS Code MCP settings:
                </p>
                <CodeBlock
                  language="json"
                  theme="auto"
                  code={`{\n  "servers": {\n    "pollenate": {\n      "type": "streamableHttp",\n      "url": "https://api.pollenate.dev/mcp",\n      "headers": {\n        "Authorization": "Bearer pk_live_YOUR_API_KEY"\n      }\n    }\n  }\n}`}
                />

                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mt-6">
                  <p className="text-blue-800 dark:text-blue-300 text-sm">
                    <strong>Authentication:</strong> Pass your Pollenate API key via the{" "}
                    <code className="bg-blue-100 dark:bg-blue-800/50 px-1 rounded">Authorization: Bearer pk_live_...</code> header
                    (or <code className="bg-blue-100 dark:bg-blue-800/50 px-1 rounded">X-Pollenate-Key</code> header)
                    when configuring your MCP client. The key is automatically used by all tools.
                    You can also pass an <code className="bg-blue-100 dark:bg-blue-800/50 px-1 rounded">apiKey</code> parameter
                    to any tool to override the connection-level key.
                  </p>
                </div>
              </div>

              <div id="ai-examples" className="mb-8">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                  Agent Examples
                </h3>

                <h4 className="font-semibold text-slate-800 dark:text-neutral-200 mb-2">
                  After resolving a support issue
                </h4>
                <CodeBlock
                  language="bash"
                  theme="auto"
                  code={`curl -X POST https://api.pollenate.dev/collect \\
  -H "Content-Type: application/json" \\
  -H "X-Pollenate-Key: YOUR_API_KEY" \\
  -d '{
    "inboxKey": "support-chat",
    "type": "thumbs",
    "score": 1,
    "comment": "User confirmed their issue was resolved",
    "context": {
      "source": "ai-agent",
      "agentName": "SupportBot",
      "topic": "password-reset"
    }
  }'`}
                />

                <h4 className="font-semibold text-slate-800 dark:text-neutral-200 mb-2 mt-6">
                  Capturing conversation sentiment
                </h4>
                <CodeBlock
                  language="bash"
                  theme="auto"
                  code={`curl -X POST https://api.pollenate.dev/collect \\
  -H "Content-Type: application/json" \\
  -H "X-Pollenate-Key: YOUR_API_KEY" \\
  -d '{
    "inboxKey": "sentiment-tracker",
    "type": "emoji",
    "score": 4,
    "context": {
      "source": "ai-agent",
      "detectedSentiment": "positive",
      "conversationTurns": 8,
      "agentModel": "claude-sonnet-4"
    }
  }'`}
                />

                <h4 className="font-semibold text-slate-800 dark:text-neutral-200 mb-2 mt-6">
                  Logging product feedback from conversation
                </h4>
                <CodeBlock
                  language="bash"
                  theme="auto"
                  code={`curl -X POST https://api.pollenate.dev/collect \\
  -H "Content-Type: application/json" \\
  -H "X-Pollenate-Key: YOUR_API_KEY" \\
  -d '{
    "inboxKey": "product-feedback",
    "type": "text",
    "comment": "User requested dark mode support — 3rd request this week",
    "context": {
      "source": "ai-agent",
      "category": "feature-request",
      "priority": "high"
    }
  }'`}
                />
              </div>

              <div className="bg-slate-50 dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">
                  Best Practices
                </h3>
                <ul className="space-y-2 text-sm text-slate-700 dark:text-neutral-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">✦</span>
                    <span>
                      <strong>Ask first</strong> — Always confirm with the user
                      before submitting feedback on their behalf.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">✦</span>
                    <span>
                      <strong>Use context</strong> — Include{" "}
                      <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                        source: "ai-agent"
                      </code>{" "}
                      in the context so teams can filter agent-submitted
                      feedback.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">✦</span>
                    <span>
                      <strong>Group with sessionId</strong> — Use the same{" "}
                      <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                        sessionId
                      </code>{" "}
                      for all feedback from one conversation.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">✦</span>
                    <span>
                      <strong>Pick the right type</strong> — Use{" "}
                      <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                        thumbs
                      </code>{" "}
                      for quick yes/no,{" "}
                      <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                        text
                      </code>{" "}
                      for qualitative feedback,{" "}
                      <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                        nps
                      </code>{" "}
                      for loyalty scores.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">✦</span>
                    <span>
                      <strong>Handle rate limits</strong> — If you receive a 429
                      response, wait for the{" "}
                      <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                        retryAfter
                      </code>{" "}
                      duration before retrying.
                    </span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Automations Overview */}
            <section id="automations" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  ⚡
                </span>
                Automations &amp; Integrations
              </h2>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> Automations let Pollenate
                  take action the moment feedback arrives — email your team, ping
                  a webhook, or kick off a Zap. Think "when a customer leaves
                  1-star feedback, create a ticket and notify the team" without
                  writing any code that runs on a schedule.
                </p>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                An <strong>automation rule</strong> is a simple recipe:{" "}
                <em>when an event happens</em> (a trigger),{" "}
                <em>optionally matching some conditions</em> (filters),{" "}
                <em>do something</em> (an action). You manage rules from{" "}
                <strong>Dashboard → Automations</strong>, or directly via the{" "}
                <a
                  href="#api-overview"
                  className="text-amber-600 dark:text-amber-400 underline"
                >
                  API
                </a>
                . Every rule execution is logged with its status, HTTP status
                code, and duration so you can debug deliveries.
              </p>

              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-50 dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-5">
                  <div className="text-2xl mb-2">📨</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                    Email
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Send a formatted notification to one or more recipients when
                    feedback comes in.
                  </p>
                </div>
                <div className="bg-slate-50 dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-5">
                  <div className="text-2xl mb-2">🔗</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                    Webhook
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Send a signed request to any public URL — with custom method,
                    headers, and body template, it can call an external API (like
                    GitHub) directly.
                  </p>
                </div>
                <div className="bg-slate-50 dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-5">
                  <div className="text-2xl mb-2">⚙️</div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                    Zapier &amp; more
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-neutral-400">
                    Use the native Zapier app (or any webhook-compatible tool) to
                    reach 7,000+ apps including Slack, Jira, and GitHub.
                  </p>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-8">
                Rule scope
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                Rules can be scoped to a single inbox or to your whole
                organization:
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">Scope</th>
                      <th className="text-left p-3 font-semibold">Fires for</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          inbox
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Feedback submitted to one specific inbox. Requires an{" "}
                        <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                          inboxId
                        </code>
                        .
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          organization
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Any inbox in the organization, plus feedback-page
                        responses. Required for the{" "}
                        <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                          page_response.created
                        </code>{" "}
                        event.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Events & Filters */}
            <section id="automation-events" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🎯
                </span>
                Events &amp; Filters
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Each rule listens for one or more <strong>events</strong> and can
                narrow down <em>which</em> of those events actually trigger the
                action using <strong>filters</strong>. An empty filter set
                matches everything.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Events
              </h3>
              <div className="overflow-x-auto mb-8">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">Event</th>
                      <th className="text-left p-3 font-semibold">
                        Fires when…
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          feedback.created
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Feedback is submitted via a widget, the API, the MCP
                        server, an import, or Zapier.
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          page_response.created
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Someone completes a Feedback Page survey.{" "}
                        <strong>Organization scope only.</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Filters for{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded text-base">
                  feedback.created
                </code>
              </h3>
              <div className="overflow-x-auto mb-8">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">Filter</th>
                      <th className="text-left p-3 font-semibold">Type</th>
                      <th className="text-left p-3 font-semibold">Matches</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    {[
                      ["type", "string | string[]", "Feedback type(s): thumbs, stars, csat, nps, emoji, text"],
                      ["source", "string | string[]", "Origin: widget, api, zapier, import, mcp"],
                      ["minScore", "number", "Score greater than or equal to value"],
                      ["maxScore", "number", "Score less than or equal to value"],
                      ["scoreEquals", "number", "Score exactly equals value"],
                      ["scoreAbove", "number", "Score strictly greater than value"],
                      ["scoreBelow", "number", "Score strictly less than value"],
                      ["hasComment", "boolean", "Feedback includes a non-empty comment"],
                      ["sentiment", '"positive" | "negative"', "Thumbs up (positive) or thumbs down (negative)"],
                      ["npsCategory", '"promoter" | "passive" | "detractor"', "NPS bucket: promoter 9–10, passive 7–8, detractor 0–6"],
                    ].map(([name, type, desc]) => (
                      <tr key={name}>
                        <td className="p-3">
                          <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                            {name}
                          </code>
                        </td>
                        <td className="p-3 text-slate-600 dark:text-neutral-400">
                          <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                            {type}
                          </code>
                        </td>
                        <td className="p-3 text-slate-600 dark:text-neutral-400">
                          {desc}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Filters for{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded text-base">
                  page_response.created
                </code>
              </h3>
              <div className="overflow-x-auto mb-8">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">Filter</th>
                      <th className="text-left p-3 font-semibold">Type</th>
                      <th className="text-left p-3 font-semibold">Matches</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          pageId
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                          string
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Only responses from a specific Feedback Page
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          hasEmail
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                          boolean
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Only responses where the respondent left an email address
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Create a rule via the API
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                Send a low-score webhook for an inbox, but only when there's a
                written comment:
              </p>
              <CodeBlock
                language="bash"
                theme="auto"
                label="Create a webhook automation"
                code={`curl -X POST https://api.pollenate.dev/organizations/ORG_ID/automations \\
  -H "Authorization: Bearer YOUR_JWT" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Low NPS to webhook",
    "scope": "inbox",
    "inboxId": "INBOX_ID",
    "type": "webhook",
    "target": "https://example.com/hooks/pollenate",
    "events": ["feedback.created"],
    "filters": {
      "type": "nps",
      "npsCategory": "detractor",
      "hasComment": true
    }
  }'`}
              />
            </section>

            {/* Webhooks */}
            <section id="webhooks" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🔗
                </span>
                Webhooks
              </h2>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> A webhook is a URL you
                  own that Pollenate calls automatically whenever something
                  happens. Pollenate "pushes" the data to you in real time, so
                  you don't have to keep asking "any new feedback yet?"
                </p>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                When a <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">webhook</code>{" "}
                rule fires, Pollenate sends an HTTP{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">POST</code>{" "}
                request to your target URL with a JSON body and a set of
                identifying headers. Deliveries time out after 10 seconds; a{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">2xx</code>{" "}
                response is treated as success and anything{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">&ge; 400</code>{" "}
                is logged as a failure.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Request headers
              </h3>
              <div className="overflow-x-auto mb-8">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">Header</th>
                      <th className="text-left p-3 font-semibold">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    {[
                      ["Content-Type", "Always application/json"],
                      ["User-Agent", "Pollenate-Webhook/1.0"],
                      ["X-Pollenate-Event", "The event name, e.g. feedback.created"],
                      ["X-Pollenate-Delivery", "A unique UUID for this delivery attempt (use for idempotency)"],
                      ["X-Pollenate-Timestamp", "ISO 8601 timestamp of when the event fired"],
                      ["X-Pollenate-Signature", "sha256=… HMAC signature — only present when a signing secret is set"],
                    ].map(([name, desc]) => (
                      <tr key={name}>
                        <td className="p-3">
                          <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                            {name}
                          </code>
                        </td>
                        <td className="p-3 text-slate-600 dark:text-neutral-400">
                          {desc}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Payload shape
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                Every payload is wrapped in an envelope with the{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                  event
                </code>
                , a <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">timestamp</code>
                , and the event-specific{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                  data
                </code>
                . Here's a{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                  feedback.created
                </code>{" "}
                payload:
              </p>
              <CodeBlock
                language="json"
                theme="auto"
                label="feedback.created payload"
                code={`{
  "event": "feedback.created",
  "timestamp": "2026-06-05T14:23:11.482Z",
  "data": {
    "feedbackId": "fb_a1b2c3",
    "inboxId": "inbox_123",
    "inboxName": "Website",
    "inboxKey": "pk_live_abc",
    "brandId": "brand_1",
    "brandName": "Acme",
    "orgId": "org_42",
    "orgName": "Acme Inc.",
    "type": "nps",
    "score": 2,
    "comment": "Checkout kept failing on mobile.",
    "pageUrl": "https://acme.com/checkout",
    "sessionId": "sess_789",
    "context": "checkout-flow",
    "source": "widget",
    "externalUserId": "user_55",
    "createdAt": "2026-06-05T14:23:11.000Z"
  }
}`}
              />

              <p className="text-slate-600 dark:text-neutral-400 mb-4 mt-6">
                And a{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                  page_response.created
                </code>{" "}
                payload:
              </p>
              <CodeBlock
                language="json"
                theme="auto"
                label="page_response.created payload"
                code={`{
  "event": "page_response.created",
  "timestamp": "2026-06-05T14:25:02.110Z",
  "data": {
    "responseId": "resp_abc",
    "pageId": "page_99",
    "pageTitle": "Onboarding Survey",
    "pageSlug": "onboarding-survey",
    "orgId": "org_42",
    "orgName": "Acme Inc.",
    "brandName": "Acme",
    "respondentName": "Jordan Lee",
    "respondentEmail": "jordan@example.com",
    "answers": [
      {
        "questionId": "q1",
        "questionPrompt": "How easy was setup?",
        "questionType": "stars",
        "score": 4,
        "textValue": null,
        "multiValues": null
      }
    ],
    "createdAt": "2026-06-05T14:25:02.000Z"
  }
}`}
              />

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-8">
                Verifying the signature
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                When you set a signing secret on the rule, Pollenate signs the{" "}
                <strong>raw request body</strong> with HMAC-SHA256 and sends the
                hex digest in the{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                  X-Pollenate-Signature
                </code>{" "}
                header as{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                  sha256=&lt;digest&gt;
                </code>
                . Recompute it on your side and compare to confirm the request
                genuinely came from Pollenate and wasn't tampered with.
              </p>
              <CodeBlock
                language="javascript"
                theme="auto"
                label="Verify in Node.js / Express"
                code={`import crypto from "node:crypto";

const SECRET = process.env.POLLENATE_WEBHOOK_SECRET;

// Use the RAW body, not the parsed JSON object.
app.post("/hooks/pollenate", express.raw({ type: "application/json" }), (req, res) => {
  const signature = req.header("X-Pollenate-Signature") || "";
  const expected =
    "sha256=" +
    crypto.createHmac("sha256", SECRET).update(req.body).digest("hex");

  const ok =
    signature.length === expected.length &&
    crypto.timingSafeEqual(Buffer.from(signature), Buffer.from(expected));

  if (!ok) return res.status(401).send("Invalid signature");

  const payload = JSON.parse(req.body.toString("utf8"));
  console.log("Verified Pollenate event:", payload.event);
  res.sendStatus(200);
});`}
              />

              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/40 rounded-xl p-6 mt-8">
                <h3 className="font-semibold text-amber-900 dark:text-amber-300 mb-2">
                  Target URLs must be public
                </h3>
                <p className="text-amber-800 dark:text-amber-300 text-sm">
                  To prevent server-side request forgery (SSRF), Pollenate only
                  delivers to publicly reachable hosts. Webhooks pointed at{" "}
                  <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                    localhost
                  </code>
                  , loopback, link-local, or private IP ranges (
                  <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                    10.x
                  </code>
                  ,{" "}
                  <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                    192.168.x
                  </code>
                  ,{" "}
                  <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                    172.16–31.x
                  </code>
                  ) are rejected. For local development, use a tunnel such as{" "}
                  <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                    ngrok
                  </code>{" "}
                  or{" "}
                  <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                    cloudflared
                  </code>
                  .
                </p>
              </div>

              <div className="bg-slate-50 dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6 mt-6">
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                  Test &amp; debug
                </h3>
                <p className="text-sm text-slate-700 dark:text-neutral-300">
                  Use the <strong>Test</strong> button on any rule in{" "}
                  <strong>Dashboard → Automations</strong> to fire a sample
                  payload, then open <strong>Logs</strong> on the rule to see
                  each delivery's status, HTTP status code, duration, and any
                  error message.
                </p>
              </div>
            </section>

            {/* Secrets & Custom Headers */}
            <section id="webhook-secrets" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🔐
                </span>
                Secrets &amp; Custom Headers
              </h2>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> Most APIs (GitHub, Slack,
                  Jira…) require an authentication token. Pollenate lets you save
                  those tokens once in an encrypted <strong>secrets store</strong>
                  , then drop them into your webhook's headers — so a webhook can
                  call another service directly, no middleman required.
                </p>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Webhook automations support three extra knobs that turn a basic
                webhook into a full HTTP request to any API:
              </p>
              <ul className="space-y-3 text-slate-600 dark:text-neutral-400 mb-8">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">✦</span>
                  <span>
                    <strong>HTTP method</strong> — choose{" "}
                    <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                      POST
                    </code>
                    ,{" "}
                    <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                      PUT
                    </code>
                    ,{" "}
                    <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                      PATCH
                    </code>
                    ,{" "}
                    <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                      DELETE
                    </code>
                    , or{" "}
                    <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                      GET
                    </code>
                    .
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">✦</span>
                  <span>
                    <strong>Custom headers</strong> — add any request headers,
                    e.g. an{" "}
                    <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                      Authorization
                    </code>{" "}
                    token or a specific{" "}
                    <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                      Accept
                    </code>
                    /
                    <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                      Content-Type
                    </code>
                    .
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">✦</span>
                  <span>
                    <strong>Body template</strong> — shape the request body
                    exactly how the target API expects, using placeholders for
                    event data.
                  </span>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                The secrets store
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                Create named secrets under{" "}
                <strong>Dashboard → Automations → Manage secrets</strong> (or via
                the API). Each secret is encrypted at rest with AES-256-GCM and
                its value is <strong>never returned</strong> by the API again —
                you'll only ever see a masked hint like{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                  ••••cdef
                </code>
                . Reference a secret anywhere in your headers or body template
                with{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                  {"{{secrets.NAME}}"}
                </code>
                .
              </p>
              <CodeBlock
                language="bash"
                theme="auto"
                label="Create a secret via the API"
                code={`curl -X POST https://api.pollenate.dev/organizations/ORG_ID/secrets \\
  -H "Authorization: Bearer YOUR_JWT" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "GITHUB_TOKEN",
    "value": "github_pat_11AAA...",
    "description": "Fine-grained PAT, Issues: read & write"
  }'`}
              />

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-8">
                Templating
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                Header values and the body template support{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                  {"{{ ... }}"}
                </code>{" "}
                placeholders. This is plain text substitution — there is no code
                execution — resolved against the event at delivery time:
              </p>
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">Placeholder</th>
                      <th className="text-left p-3 font-semibold">Resolves to</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    {[
                      ["{{secrets.NAME}}", "A decrypted value from your secrets store"],
                      ["{{event}}", "The event name, e.g. feedback.created"],
                      ["{{timestamp}}", "ISO 8601 time the event fired"],
                      ["{{data.comment}}", "Any field from the event payload's data object"],
                      ["{{data.score}}", "Numbers and booleans are coerced to text"],
                    ].map(([ph, desc]) => (
                      <tr key={ph}>
                        <td className="p-3">
                          <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                            {ph}
                          </code>
                        </td>
                        <td className="p-3 text-slate-600 dark:text-neutral-400">
                          {desc}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-2">
                Unknown placeholders resolve to an empty string. See the{" "}
                <a
                  href="#automation-events"
                  className="text-amber-600 dark:text-amber-400 underline"
                >
                  payload reference
                </a>{" "}
                for every available{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                  data.*
                </code>{" "}
                field.
              </p>
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/40 rounded-xl p-6 mt-6">
                <h3 className="font-semibold text-amber-900 dark:text-amber-300 mb-2">
                  Good to know
                </h3>
                <ul className="space-y-1.5 text-amber-800 dark:text-amber-300 text-sm list-disc list-inside">
                  <li>
                    When a body template is set, the{" "}
                    <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                      X-Pollenate-Signature
                    </code>{" "}
                    HMAC is computed over the <em>rendered</em> body.
                  </li>
                  <li>
                    Reserved headers (
                    <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                      Host
                    </code>
                    ,{" "}
                    <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                      Content-Length
                    </code>
                    , and the{" "}
                    <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                      X-Pollenate-*
                    </code>{" "}
                    signature/delivery headers) can't be overridden.
                  </li>
                  <li>
                    The public-URL/SSRF restriction still applies — targets must
                    be publicly reachable.
                  </li>
                </ul>
              </div>
            </section>

            {/* Recipe: GitHub Issue */}
            <section id="webhook-github-issue" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🐙
                </span>
                Recipe: Create a GitHub Issue
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                A common ask: "when a customer leaves negative feedback,{" "}
                <strong>open a GitHub issue</strong> so the team can triage it."
                With secrets and custom headers you can do this with a webhook
                that calls GitHub <em>directly</em> — no relay or extra service
                needed.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Option A — Direct webhook (recommended)
              </h3>
              <ol className="space-y-2 text-slate-600 dark:text-neutral-400 mb-6 list-decimal list-inside">
                <li>
                  In <strong>Manage secrets</strong>, add a secret named{" "}
                  <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                    GITHUB_TOKEN
                  </code>{" "}
                  containing a fine-grained PAT with{" "}
                  <em>Issues: Read &amp; write</em> on the target repo.
                </li>
                <li>
                  Create a <strong>webhook</strong> automation for{" "}
                  <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                    feedback.created
                  </code>{" "}
                  with a filter such as{" "}
                  <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                    scoreBelow: 3
                  </code>
                  .
                </li>
                <li>
                  Set the URL, method, headers, and body template as shown below.
                </li>
              </ol>
              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    <tr>
                      <td className="p-3 font-semibold align-top w-28">URL</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded break-all">
                          https://api.github.com/repos/OWNER/REPO/issues
                        </code>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-semibold align-top">Method</td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          POST
                        </code>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 font-semibold align-top">Headers</td>
                      <td className="p-3 space-y-1">
                        <div>
                          <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded break-all">
                            Authorization: Bearer {"{{secrets.GITHUB_TOKEN}}"}
                          </code>
                        </div>
                        <div>
                          <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded break-all">
                            Accept: application/vnd.github+json
                          </code>
                        </div>
                        <div>
                          <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded break-all">
                            User-Agent: pollenate
                          </code>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-3">
                Body template:
              </p>
              <CodeBlock
                language="json"
                theme="auto"
                label="Body template → GitHub create issue"
                code={`{
  "title": "Feedback: {{data.type}} ({{data.score}}) on {{data.inboxName}}",
  "body": "**Comment:** {{data.comment}}\\n\\n**Score:** {{data.score}}\\n**Source:** {{data.source}}\\n**Page:** {{data.pageUrl}}\\n**Feedback ID:** {{data.feedbackId}}",
  "labels": ["feedback", "needs-triage"]
}`}
              />
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mt-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>Tip:</strong> Hit <strong>Test</strong> on the rule to
                  send a sample issue, then check <strong>Logs</strong> for
                  GitHub's HTTP status. The same recipe works for Linear, Jira,
                  Asana, or Slack — just change the URL, headers, and body
                  template. Make sure{" "}
                  <code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">
                    Content-Type
                  </code>{" "}
                  matches what the API expects (GitHub accepts JSON by default).
                </p>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-10">
                Option B — No code (Zapier)
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-3">
                Prefer not to manage tokens at all? Use the{" "}
                <a
                  href="#zapier"
                  className="text-amber-600 dark:text-amber-400 underline"
                >
                  Zapier
                </a>{" "}
                app: a <strong>New Feedback</strong> trigger plus GitHub's{" "}
                <strong>Create Issue</strong> action, with a filter for negative
                feedback.
              </p>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-10">
                Option C — Webhook + relay
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                If you'd rather keep credentials entirely in your own
                infrastructure, send the webhook to a small relay (here, a
                Cloudflare Worker) that verifies the signature and calls the{" "}
                <a
                  href="https://docs.github.com/en/rest/issues/issues#create-an-issue"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-600 dark:text-amber-400 underline"
                >
                  GitHub REST API
                </a>
                .
              </p>
              <CodeBlock
                language="javascript"
                theme="auto"
                label="Cloudflare Worker relay → GitHub issue"
                code={`export default {
  async fetch(request, env) {
    if (request.method !== "POST") return new Response("Method not allowed", { status: 405 });

    // 1. Read the RAW body so the signature still matches.
    const raw = await request.text();

    // 2. Verify the Pollenate signature (optional but recommended).
    const signature = request.headers.get("X-Pollenate-Signature") || "";
    const key = await crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(env.POLLENATE_WEBHOOK_SECRET),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"],
    );
    const mac = await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(raw));
    const expected =
      "sha256=" +
      [...new Uint8Array(mac)].map((b) => b.toString(16).padStart(2, "0")).join("");
    if (signature !== expected) return new Response("Invalid signature", { status: 401 });

    // 3. Only act on negative feedback.
    const { event, data } = JSON.parse(raw);
    if (event !== "feedback.created" || (data.score ?? 99) > 3) {
      return new Response("Ignored", { status: 200 });
    }

    // 4. Create the GitHub issue.
    const res = await fetch("https://api.github.com/repos/OWNER/REPO/issues", {
      method: "POST",
      headers: {
        Authorization: \`Bearer \${env.GITHUB_TOKEN}\`,
        Accept: "application/vnd.github+json",
        "User-Agent": "pollenate-relay",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        title: \`Feedback: \${data.type} (\${data.score}) on \${data.inboxName}\`,
        body: [
          \`**Comment:** \${data.comment || "(none)"}\`,
          \`**Score:** \${data.score}\`,
          \`**Source:** \${data.source}\`,
          \`**Page:** \${data.pageUrl || "n/a"}\`,
          \`**Feedback ID:** \${data.feedbackId}\`,
        ].join("\\n"),
        labels: ["feedback", "needs-triage"],
      }),
    });

    return new Response(res.ok ? "Issue created" : "GitHub error", {
      status: res.ok ? 200 : 502,
    });
  },
};`}
              />
            </section>

            {/* Zapier */}
            <section id="zapier" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  ⚙️
                </span>
                Zapier
              </h2>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> Zapier is a no-code tool
                  that connects apps together. With the Pollenate Zapier app you
                  can route feedback to Slack, Google Sheets, Jira, GitHub, your
                  CRM, and 7,000+ other apps — without writing any code.
                </p>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Connect your account by creating an API key with the{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                  read
                </code>{" "}
                +{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                  write
                </code>{" "}
                scopes at{" "}
                <a
                  href="/api-keys?integration=zapier"
                  className="text-amber-600 dark:text-amber-400 underline"
                >
                  pollenate.dev/api-keys
                </a>{" "}
                (the link pre-selects the right scopes), then paste it into
                Zapier when prompted. Triggers use REST Hooks, so feedback flows
                through in real time rather than on a polling delay.
              </p>

              <div className="grid sm:grid-cols-3 gap-6 mb-6">
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                    Triggers
                  </h3>
                  <ul className="text-sm text-slate-600 dark:text-neutral-400 space-y-1 list-disc list-inside">
                    <li>New Feedback</li>
                    <li>New Page Response</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                    Actions
                  </h3>
                  <ul className="text-sm text-slate-600 dark:text-neutral-400 space-y-1 list-disc list-inside">
                    <li>Submit Feedback</li>
                    <li>Add Note to Feedback</li>
                    <li>Update Feedback (link Jira / GitHub / Linear)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">
                    Searches
                  </h3>
                  <ul className="text-sm text-slate-600 dark:text-neutral-400 space-y-1 list-disc list-inside">
                    <li>Find Feedback</li>
                    <li>Get Feedback by ID</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Example Zaps
              </h3>
              <ul className="space-y-2 text-slate-600 dark:text-neutral-400 mb-4 list-disc list-inside">
                <li>
                  <strong>Feedback → Slack:</strong> post a message when NPS
                  detractors leave a comment.
                </li>
                <li>
                  <strong>Feedback → GitHub/Jira:</strong> auto-create an issue
                  for negative feedback.
                </li>
                <li>
                  <strong>Feedback → Google Sheets:</strong> log every entry to a
                  spreadsheet for analysis.
                </li>
                <li>
                  <strong>Typeform → Pollenate:</strong> pipe survey responses in
                  as feedback.
                </li>
              </ul>
            </section>

            {/* More Integrations */}
            <section id="integrations" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🧩
                </span>
                More Integrations
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                Because automations support generic webhooks, you can connect
                Pollenate to nearly anything:
              </p>
              <ul className="space-y-3 text-slate-600 dark:text-neutral-400 mb-6">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">✦</span>
                  <span>
                    <strong>Make, n8n, Pipedream:</strong> point a webhook rule at
                    their "catch hook" URL and build flows visually — same payload
                    and signature as described above.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">✦</span>
                  <span>
                    <strong>Slack:</strong> use the Zapier "Slack → Send Channel
                    Message" action, or relay through your own endpoint that posts
                    to a Slack Incoming Webhook (Slack expects its own{" "}
                    <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                      {"{ text }"}
                    </code>{" "}
                    body, so reshape the payload first).
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">✦</span>
                  <span>
                    <strong>Your own backend:</strong> receive webhooks directly
                    and write to your database, queue, or analytics pipeline.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">✦</span>
                  <span>
                    <strong>Email:</strong> the built-in email action delivers a
                    formatted notification with no extra tooling required.
                  </span>
                </li>
              </ul>
              <div className="bg-slate-50 dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6">
                <p className="text-sm text-slate-700 dark:text-neutral-300">
                  Want to push data <em>into</em> Pollenate instead? Use the{" "}
                  <a
                    href="#api-feedback"
                    className="text-amber-600 dark:text-amber-400 underline"
                  >
                    Feedback API
                  </a>
                  , the{" "}
                  <a
                    href="#ai-mcp-server"
                    className="text-amber-600 dark:text-amber-400 underline"
                  >
                    MCP server
                  </a>
                  , or the Zapier <strong>Submit Feedback</strong> action.
                </p>
              </div>
            </section>

            {/* Content Security Policy */}
            <section id="content-security-policy" className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-3">
                <span className="bg-amber-100 dark:bg-amber-900 text-amber-600 dark:text-amber-400 w-10 h-10 rounded-lg flex items-center justify-center text-lg">
                  🛡️
                </span>
                Content Security Policy (CSP)
              </h2>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-700/40 rounded-xl p-4 mb-6">
                <p className="text-blue-800 dark:text-blue-300 text-sm">
                  <strong>🗣️ In plain English:</strong> Some websites have strict security rules that block outside tools from running.
                  If your widget isn't working, this might be why. Ask your web developer to check if your site
                  has a "Content Security Policy" — if so, they'll need to add Pollenate to the approved list. The instructions below show them exactly what to do.
                </p>
              </div>
              <p className="text-slate-600 dark:text-neutral-400 mb-6">
                If your website uses a Content Security Policy, you'll need to
                allow the Pollenate widget script and API endpoints. Without the
                correct CSP directives, the widget will load but fail to submit
                feedback or track impressions.
              </p>

              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-700/40 rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-red-900 dark:text-red-300 mb-2">
                  Common Error
                </h3>
                <p className="text-red-800 dark:text-red-300 text-sm mb-3">
                  If you see errors like the following in your browser console,
                  your CSP is blocking the Pollenate widget:
                </p>
                <CodeBlock
                  language="text"
                  theme="auto"
                  code={`Refused to connect to 'https://api.pollenate.dev/collect' because it violates the document's Content Security Policy.

[Pollenate] Error: TypeError: Failed to fetch. Refused to connect because it violates the document's Content Security Policy.`}
                />
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Required CSP Directives
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                Add the following origins to your Content Security Policy to
                allow the Pollenate widget to function correctly:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-neutral-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">Directive</th>
                      <th className="text-left p-3 font-semibold">Value</th>
                      <th className="text-left p-3 font-semibold">Purpose</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-neutral-800">
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          script-src
                        </code>
                      </td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          https://pollenate.dev
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Loads the widget script
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          connect-src
                        </code>
                      </td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          https://api.pollenate.dev
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        Submits feedback and tracks impressions via the API
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          style-src
                        </code>
                      </td>
                      <td className="p-3">
                        <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                          'unsafe-inline'
                        </code>
                      </td>
                      <td className="p-3 text-slate-600 dark:text-neutral-400">
                        The widget injects inline styles for its UI
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                Example: HTTP Header
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                If you set your CSP via an HTTP response header, add the
                Pollenate origins to the relevant directives:
              </p>
              <CodeBlock
                language="text"
                theme="auto"
                label="Content-Security-Policy header"
                code={`Content-Security-Policy: 
  script-src 'self' https://pollenate.dev; 
  connect-src 'self' https://api.pollenate.dev; 
  style-src 'self' 'unsafe-inline';`}
              />

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-8">
                Example: HTML Meta Tag
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                If you set your CSP via a{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                  &lt;meta&gt;
                </code>{" "}
                tag, include the Pollenate origins:
              </p>
              <CodeBlock
                language="html"
                theme="auto"
                code={`<meta http-equiv="Content-Security-Policy" content="
  script-src 'self' https://pollenate.dev; 
  connect-src 'self' https://api.pollenate.dev; 
  style-src 'self' 'unsafe-inline';
">`}
              />

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-8">
                Example: Nginx
              </h3>
              <CodeBlock
                language="nginx"
                theme="auto"
                code={`add_header Content-Security-Policy "
  script-src 'self' https://pollenate.dev; 
  connect-src 'self' https://api.pollenate.dev; 
  style-src 'self' 'unsafe-inline';
" always;`}
              />

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-8">
                Example: Apache
              </h3>
              <CodeBlock
                language="apache"
                theme="auto"
                code={`Header set Content-Security-Policy "\
  script-src 'self' https://pollenate.dev; \
  connect-src 'self' https://api.pollenate.dev; \
  style-src 'self' 'unsafe-inline';\
"`}
              />

              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-8">
                Example: Next.js
              </h3>
              <p className="text-slate-600 dark:text-neutral-400 mb-4">
                In your{" "}
                <code className="bg-slate-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                  next.config.js
                </code>
                :
              </p>
              <CodeBlock
                language="javascript"
                theme="auto"
                code={`const securityHeaders = [
  {
    key: 'Content-Security-Policy',
    value: [
      "script-src 'self' https://pollenate.dev",
      "connect-src 'self' https://api.pollenate.dev",
      "style-src 'self' 'unsafe-inline'",
    ].join('; '),
  },
];

module.exports = {
  async headers() {
    return [{ source: '/(.*)', headers: securityHeaders }];
  },
};`}
              />

              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700/40 rounded-xl p-6 mt-8">
                <h3 className="font-semibold text-amber-900 dark:text-amber-300 mb-2">
                  Tip: Custom API URL
                </h3>
                <p className="text-amber-800 dark:text-amber-300 text-sm">
                  If you use the{" "}
                  <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                    data-api-url
                  </code>{" "}
                  attribute to point the widget at a custom API endpoint, make
                  sure to add that URL to your{" "}
                  <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                    connect-src
                  </code>{" "}
                  directive instead of (or in addition to){" "}
                  <code className="bg-amber-100 dark:bg-amber-800/50 dark:text-amber-300 px-1 rounded">
                    https://api.pollenate.dev
                  </code>
                  .
                </p>
              </div>

              <div className="bg-slate-50 dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6 mt-6">
                <h3 className="font-semibold text-slate-900 dark:text-white mb-3">
                  How to Verify
                </h3>
                <ol className="space-y-2 text-sm text-slate-700 dark:text-neutral-300 list-decimal list-inside">
                  <li>
                    Open your browser's Developer Tools (F12 or Cmd+Opt+I).
                  </li>
                  <li>
                    Go to the <strong>Console</strong> tab and look for CSP
                    violation errors.
                  </li>
                  <li>
                    Check the <strong>Network</strong> tab — blocked requests to{" "}
                    <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                      api.pollenate.dev
                    </code>{" "}
                    will show as{" "}
                    <code className="bg-slate-100 dark:bg-neutral-800 px-1 rounded">
                      (blocked:csp)
                    </code>
                    .
                  </li>
                  <li>
                    After updating your CSP, hard-refresh the page (Ctrl+Shift+R
                    / Cmd+Shift+R) and confirm the errors are gone.
                  </li>
                  <li>
                    Submit a test feedback entry and verify it appears in your
                    Pollenate dashboard.
                  </li>
                </ol>
              </div>
            </section>

            {/* FAQ */}
            <FAQSection
              faqs={docsFAQs}
              title="Developer FAQ"
              subtitle="Common questions about integrating and using the Pollenate API and widgets."
              id="docs-faq"
            />

            {/* CTA */}
            <section className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 rounded-2xl p-8 text-center text-white">
              <h2 className="text-2xl font-bold mb-4">
                Ready to start collecting feedback?
              </h2>
              <p className="text-white/90 mb-6 max-w-md mx-auto">
                Create your free account and have your first widget live in
                minutes.
              </p>
              <Link to="/login?mode=signup">
                <Button className="bg-white dark:bg-neutral-900 text-amber-600 dark:text-amber-400 hover:bg-slate-100 dark:bg-neutral-800">
                  Get Started Free
                  <span className="ml-2">→</span>
                </Button>
              </Link>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-neutral-800 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <BrandLockup
              iconClassName="h-5 w-5"
              textClassName="font-semibold text-slate-900 dark:text-white"
            />
            <div className="flex items-center gap-6 text-sm text-slate-600 dark:text-neutral-400">
              <Link
                to="/terms"
                className="hover:text-slate-900 dark:hover:text-white"
              >
                Terms
              </Link>
              <Link
                to="/privacy"
                className="hover:text-slate-900 dark:hover:text-white"
              >
                Privacy
              </Link>
              <a
                href="mailto:support@pollenate.dev"
                className="hover:text-slate-900 dark:hover:text-white"
              >
                Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
